import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { siteWide } from '@helpers/grid';
import { bodyLg, bodyMd, bodySm, bodyXs, responsiveStyle } from '@helpers/typography';
import { between, ButtonReset, Device, fluid, from, transition, until } from '@netc/style-utils';
import Textbox from '@stories/Components/Misc/Forms/Inputs/Textbox/Textbox';
import { transparentize } from 'polished';
import styled, { createGlobalStyle, css } from 'styled-components';
import MagnifyingGlass from '@img/icons/magnifying-glass.svg?url';
import MagnifyingGlassWhite from '@img/icons/magnifying-glass-white.svg?url';
const HeaderGlobalStyles = createGlobalStyle `
    :root {
      --mainHeaderHeight: ${fluid(64, 88)} !important;
    }

    html {
      padding-top: var(--headerHeight);
    }
`;
const Container = styled.header `
  position: fixed;
  inset: 0;
  height: var(--mainHeaderHeight, 0px);
  --bg: ${brand.ngbBlue.default};
  --fg: ${brand.global.white};
  z-index: 1000;
  --boxShadowColour: transparent;
  box-shadow: 0px 8px 8px 0px var(--boxShadowColour);
  ${transition('background-color, color, box-shadow', 0.15, 'ease')};

  background-color: var(--bg);
  color: var(--fg);

  &[data-theme='white'] {
    --bg: ${brand.global.white};
    --fg: ${brand.ngbBlue.default};
  }

  &[data-has-announcement-bar='true'] {
    height: calc(var(--mainHeaderHeight, 0px) + var(--announcementBarHeight, 0px));
  }

  &[data-has-scrolled-down='true'] {
    --boxShadowColour: #0000000a;
  }
`;
const Inner = styled.div `
  ${siteWide};

  height: 100%;
  padding: 24px var(--sitePadding);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media ${until(Device.DesktopSmall)} {
    ${transition('background-color, color', 0.25, 'ease')};

    background-color: var(--bg);
    color: var(--fg);
  }

  [data-has-announcement-bar='true'] & {
    height: calc(100% - var(--announcementBarHeight, 0px));
  }
`;
const LeftContent = styled.div `
  display: flex;
  flex-shrink: 0;
  gap: ${fluid(20, 40, Device.DesktopLarge, Device.ActualDesktop)};

  > a[data-home-link] {
    max-width: ${fluid(104, 125, Device.Tablet, Device.TabletLarge)};
    width: 100%;
    display: block;
    color: inherit;

    > div {
      &[data-full-logo] {
        @media ${between(Device.DesktopSmall, Device.DesktopLarge)} {
          display: none;
        }
      }

      &[data-solo-logo] {
        @media ${until(Device.DesktopSmall)} {
          display: none;
        }

        @media ${from(Device.DesktopLarge)} {
          display: none;
        }
      }

      > svg {
        width: 100%;
        height: 100%;
      }
    }
    /* 
    @media ${between(Device.DesktopSmall, Device.DesktopLarge)} {
      max-width: 32px;
    } */

    @media ${from(Device.DesktopLarge)} {
      max-width: 130px;
    }
  }
`;
const Nav = styled.nav `
  display: flex;
  gap: ${fluid(15, 28, Device.DesktopLarge, Device.ActualDesktop)};

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }

  .focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
  }
`;
const NavTrigger = styled.button `
  ${ButtonReset};
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--fg);
  position: relative;
  text-decoration: none;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: ${fluid(8, 12, Device.DesktopSmall, Device.Desktop)};
    width: 0px;
    height: 1px;
    background-color: ${brand.skyBlue.default};
    ${transition('width', 0.15, 'ease-in-out')};
  }

  > span {
    ${responsiveStyle(bodyMd, bodyLg, Device.DesktopLarge, fonts.montserrat.bold)};
  }

  > div {
    width: 8px;
    height: 8px;
    position: relative;
    color: ${brand.skyBlue.default};

    > svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      ${transition('opacity, transform', 0.15, 'ease-in-out')};

      &[data-nav-icon='x'] {
        opacity: 1;
        transform: rotate(45deg);
      }
    }
  }

  &[data-active='true'] {
    &::after {
      width: 100%;
    }
  }

  &:not(:hover) {
    &[data-active='true'] {
      [data-nav-icon='x'] {
        transform: rotate(0deg);
      }
    }
  }

  &:hover {
    &::after {
      width: 100%;
    }

    &[data-active='false'] {
      [data-nav-icon='dot'] {
        opacity: 1;
      }

      [data-nav-icon='x'] {
        opacity: 0;
      }
    }

    &[data-active='true'] {
      [data-nav-icon='dot'] {
        opacity: 0;
      }
      [data-nav-icon='x'] {
        transform: rotate(0deg);
        opacity: 1;
      }
    }
  }
`;
const ActiveBackground = styled.div `
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: ${transparentize(0.2, brand.global.black)};
  backdrop-filter: blur(4px);
  z-index: 998;
  pointer-events: none;
  opacity: 0;
  ${transition('opacity', 0.2, 'ease-in-out')};

  &[data-visible='true'] {
    opacity: 1;
    pointer-events: auto;
  }
`;
const RightContent = styled.div `
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: flex-end;
  width: 100%;

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
export const HeaderTextbox = styled(Textbox) `
  height: 40px;
  --bgColour: ${transparentize(0.9, brand.ngbBlue.default)};
  --textColour: ${brand.ngbBlue.default};
  --placeholderColour: ${transparentize(0.28, brand.ngbBlue.default)};
  --iconSize: 17px;
  --paddingY: 0px;
  border: none;

  [data-theme='blue'] & {
    --textColour: ${brand.global.white};
    --placeholderColour: ${transparentize(0.28, brand.global.white)};
    --bgColour: ${transparentize(0.9, brand.global.white)};
    background-image: url(${MagnifyingGlassWhite});
  }

  &::placeholder {
    @media ${between(Device.DesktopSmall, Device.DesktopLarge)} {
      font-size: 80%;
    }
  }

  ${responsiveStyle(bodySm, bodyMd, Device.DesktopLarge, fonts.montserrat.medium)};

  background-color: var(--bgColour);
  background-image: url(${MagnifyingGlass});
  background-repeat: no-repeat;
  background-size: var(--iconSize) var(--iconSize);
  background-position: calc(100% - var(--paddingX)) center;
  padding: var(--paddingY) calc(var(--paddingX) * 2 + var(--iconSize) / 2) var(--paddingY)
    var(--paddingX);

  @media ${until(Device.DesktopSmall)} {
    // Weird issue but it doesn't want to conform to the max width of the box on the burger menu, need to explicitly set it... //
    flex-shrink: 0;
    max-width: calc(
      100vw - (var(--sitePadding) * 2) - (var(--paddingX) * 2 + var(--iconSize) / 2) -
        var(--paddingX)
    );
  }

  @media ${between(Device.DesktopSmall, Device.DesktopLarge)} {
    max-width: ${fluid(150, 250, Device.DesktopSmall, Device.DesktopLarge)};
  }

  @media ${from(Device.DesktopLarge)} {
    flex-grow: 1;
    max-width: ${fluid(250, 400, Device.DesktopLarge, Device.ActualDesktop)};
  }
`;
const BurgerTrigger = styled.button `
  ${ButtonReset}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--fg);

  > span {
    ${responsiveStyle(bodyXs, bodySm, Device.TabletLarge, fonts.montserrat.medium, css `
        line-height: 1;
      `)};
  }

  > svg {
    --size: 16px;
    width: var(--size);
    height: var(--size);

    @media ${from(Device.TabletLarge)} {
      --size: 20px;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const HeaderStyles = {
    HeaderGlobalStyles,
    Container,
    Inner,
    LeftContent,
    Nav,
    NavTrigger,
    ActiveBackground,
    RightContent,
    HeaderTextbox,
    BurgerTrigger,
};
export default HeaderStyles;
